import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight, TrendingUp, FileText, MessageSquare, Route, Briefcase, Sparkles,
  Shield, Crown, Sun, Moon, Check, Star, Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { AUTH_RETURNING_KEY } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import BrandMark from "@/components/BrandMark";
import LegalFooter from "@/components/legal/LegalFooter";
import { track, PRICING_EVENTS, CURRENCY_EVENTS } from "@/lib/analytics";
import { api } from "@/lib/api";

// ------------ Static plan copy -----------
// Prices come from GET /api/public/pricing (localized, with regional overrides).
// Feature lists + CTA labels are UI copy that lives in the frontend since they
// rarely change independently of design.
const PLAN_COPY = {
  free: {
    id: "free",
    name: "Free",
    cta: "Get Started Free",
    highlight: false,
    features: [
      "AI Career Chat (limited)",
      "Resume Builder",
      "Resume Analysis",
      "Career Roadmap",
      "Basic Interview Practice",
      "Job Search",
      "Salary Insights",
      "Basic Financial Planning",
      "Progress Tracking",
    ],
  },
  pro: {
    id: "pro",
    name: "Pro",
    cta: "Upgrade to Pro",
    highlight: true,
    features: [
      "Everything in Free, plus:",
      "Unlimited AI Chat",
      "Advanced Resume Analysis",
      "AI Resume Optimization",
      "AI Cover Letter Generator",
      "Unlimited Interview Practice",
      "Advanced Career Roadmaps",
      "Salary Negotiation Assistant",
      "Advanced Financial Planning",
      "Investment Planning",
      "Tax Optimization",
      "Career ROI Analysis",
      "AI Certification Recommendations",
      "Portfolio Builder",
      "Priority AI Responses",
    ],
  },
  premium: {
    id: "premium",
    name: "Premium",
    cta: "Go Premium",
    highlight: false,
    features: [
      "Everything in Pro, plus:",
      "Unlimited Everything",
      "Executive Career Strategist",
      "Voice Interview Simulator",
      "Executive Resume Review",
      "Executive Branding",
      "Advanced Financial Modeling",
      "FIRE Planning",
      "Monte Carlo Simulations",
      "Long-Term Wealth Planning",
      "Early Access to New Features",
      "Fastest AI Processing",
      "Premium Support",
    ],
  },
};

const VALID_PERIODS = new Set(["monthly", "yearly"]);
const STORAGE_KEY = "cos:landing:period";
const CURRENCY_STORAGE_KEY = "cos:pricing-currency";

function readInitialPeriod() {
  // 1. URL hash-query wins (e.g. `?period=yearly` — supports back/forward nav)
  if (typeof window !== "undefined") {
    try {
      const usp = new URLSearchParams(window.location.search);
      const q = usp.get("period");
      if (q && VALID_PERIODS.has(q)) return q;
    } catch {
      /* noop */
    }
    // 2. sessionStorage (previous toggle within the same tab)
    try {
      const s = window.sessionStorage.getItem(STORAGE_KEY);
      if (s && VALID_PERIODS.has(s)) return s;
    } catch {
      /* noop */
    }
  }
  // 3. Default → Monthly (per user spec)
  return "monthly";
}

function readInitialCurrency() {
  if (typeof window === "undefined") return null;
  // 1. URL param wins (invalid values silently ignored — the API will fall
  //    through to auto-detect if we pass an unknown code).
  try {
    const usp = new URLSearchParams(window.location.search);
    const q = usp.get("currency");
    if (q && /^[A-Z]{3}$/i.test(q)) return q.toUpperCase();
  } catch {
    /* noop */
  }
  // 2. Persisted manual selection.
  try {
    const s = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (s && /^[A-Z]{3}$/.test(s)) return s;
  } catch {
    /* noop */
  }
  return null; // let the API auto-detect
}

// Client-side Intl formatter. Handles 0-decimal currencies (JPY, KRW, IDR, VND).
const ZERO_DECIMAL = new Set(["JPY", "KRW", "IDR", "VND", "HUF", "CLP", "ISK"]);
function formatMoney(amount, currency, locale, opts = {}) {
  const dec = ZERO_DECIMAL.has(currency) ? 0 : (opts.decimals ?? 2);
  try {
    return new Intl.NumberFormat(locale || "en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(amount || 0);
  } catch {
    return `${currency} ${(amount || 0).toFixed(dec)}`;
  }
}

// ------------ FAQ config -----------------
const FAQS = [
  {
    q: "What is Career OS?",
    a: "Career OS is an AI-powered career operating system that acts as your personal career advisor, salary analyst, resume coach, interview trainer, and financial planner — all in one place. It's not a job board or a chatbot; it's an integrated system that tracks your worth over time and engineers your next move.",
  },
  {
    q: "Is there a free version?",
    a: "Yes. Free includes AI Career Chat (limited), Resume Builder, Resume Analysis, Career Roadmap, Basic Interview Practice, Job Search, Salary Insights, Basic Financial Planning, and Progress Tracking. No credit card required to start.",
  },
  {
    q: "What's the difference between Pro and Premium?",
    a: "Pro ($25/mo) unlocks unlimited AI chat, advanced resume tooling, unlimited interview practice, advanced financial planning, career ROI analysis, and priority AI responses. Premium ($80/mo) adds the Executive Career Strategist, Voice Interview Simulator, FIRE planning, Monte Carlo simulations, executive branding and resume review, plus the fastest AI processing and premium support.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from your account settings in one click. Your Pro/Premium features stay active until the end of your current billing period, and your data always remains accessible on the Free tier.",
  },
  {
    q: "Does Career OS work internationally?",
    a: "Absolutely. Career OS supports users worldwide with country-aware tax modeling (US, Canada, UK, Australia, India and more coming), multi-currency financial planning, and localized salary benchmarks. Your reporting currency follows your profile — set it once and every dashboard, projection, and AI insight speaks your language.",
  },
  {
    q: "Is my information secure?",
    a: "Yes. All data is encrypted in transit and at rest. We never sell your data, never share it with employers, and never use it to train third-party models. Full details are in our Privacy Policy and Security page.",
  },
  {
    q: "Does Career OS replace a career coach?",
    a: "For 90% of the tactical work — resume rewrites, salary benchmarking, interview prep, financial projections — yes, and instantly. For high-touch executive coaching around leadership presence and specific personal circumstances, a human coach is still valuable. Many of our Premium members use Career OS alongside a coach to make their sessions more focused and effective.",
  },
  {
    q: "How often is Career OS updated?",
    a: "Weekly. We ship new features, model upgrades, and market-data refreshes continuously. Pro and Premium members get early access to new modules before they're rolled out to Free users.",
  },
];

// ============ Component ============
export default function Landing() {
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme } = useTheme();
  const nav = useNavigate();

  // "Returning visitor" marker — persisted independently of the auth token.
  // Written on any successful login/signup. NOT cleared when the session
  // silently expires (401 from /auth/me) — only on explicit logout. So an
  // expired-token user still sees a familiar Open dashboard affordance
  // instead of being pushed back into the fresh-visitor sign-up funnel.
  const [isReturning, setIsReturning] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(AUTH_RETURNING_KEY) === "1"; }
    catch { return false; }
  });
  // Keep the flag in sync if another tab logs in / out.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === AUTH_RETURNING_KEY) {
        setIsReturning(e.newValue === "1");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  // Once auth resolves, the AuthContext may have just latched the marker
  // (e.g. /me returned 200) — re-read.
  useEffect(() => {
    if (!authLoading) {
      try {
        setIsReturning(window.localStorage.getItem(AUTH_RETURNING_KEY) === "1");
      } catch { /* noop */ }
    }
  }, [authLoading, user]);

  const [period, setPeriod] = useState(() => readInitialPeriod());

  // Currency + localized pricing state.
  // `pricingData` shape: {currency, locale, country_code, source, supported, plans}
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(false);

  // Fetch localized pricing on mount. Auto-runs again when the persisted
  // currency changes (manual selection). The API takes over auto-detection.
  useEffect(() => {
    let cancelled = false;
    const preselect = readInitialCurrency();
    setPricingLoading(true);
    setPricingError(false);
    const url = preselect ? `/public/pricing?currency=${preselect}` : "/public/pricing";
    api.get(url)
      .then((r) => {
        if (cancelled) return;
        setPricingData(r.data);
        // Only fire `currency_auto_detected` when the backend actually detected
        // (source !== "override"). Manual overrides trigger `currency_changed`
        // in the picker onSelect handler.
        if (r.data?.source && r.data.source !== "override") {
          track(CURRENCY_EVENTS.AUTO_DETECTED, {
            currency: r.data.currency,
            country_code: r.data.country_code || null,
            source: r.data.source,
            source_page: "landing",
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPricingError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setPricingLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const currency = pricingData?.currency || "USD";
  const locale = pricingData?.locale || "en-US";

  // Merge PLAN_COPY features/CTA with API prices → the shape PricingCard expects.
  const plansForRender = useMemo(() => {
    if (!pricingData?.plans) return [];
    return pricingData.plans.map((p) => {
      const copy = PLAN_COPY[p.tier] || {};
      return {
        id: p.tier,
        name: copy.name || p.name,
        cta: copy.cta || p.cta,
        highlight: copy.highlight ?? p.highlight,
        features: copy.features || p.features,
        prices: p.prices,
        currency: pricingData.currency,
        locale: pricingData.locale,
        effective_monthly_yearly: p.effective_monthly_yearly ?? null,
        yearly_savings_amount: p.yearly_savings_amount ?? null,
        yearly_savings_pct: p.yearly_savings_pct ?? null,
      };
    });
  }, [pricingData]);

  // Manual currency change → persist + refetch.
  const handleCurrencyChange = useCallback((next) => {
    const code = String(next || "").toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return;
    if (code === currency) return;
    try { window.localStorage.setItem(CURRENCY_STORAGE_KEY, code); } catch { /* noop */ }
    setPricingLoading(true);
    api.get(`/public/pricing?currency=${code}`).then((r) => {
      setPricingData(r.data);
      track(CURRENCY_EVENTS.CHANGED, {
        currency: r.data.currency,
        previous_currency: currency,
        country_code: r.data.country_code || null,
        source: "manual",
        source_page: "landing",
      });
    }).catch(() => {
      setPricingError(true);
    }).finally(() => setPricingLoading(false));
  }, [currency]);

  // Persist toggle to sessionStorage + reflect in URL (?period=…) so
  // back/forward nav restores the choice.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, period);
    } catch {
      /* noop */
    }
    try {
      const url = new URL(window.location.href);
      if (period === "monthly") {
        url.searchParams.delete("period");
      } else {
        url.searchParams.set("period", period);
      }
      window.history.replaceState(null, "", url.toString());
    } catch {
      /* noop */
    }
  }, [period]);

  // Listen for back/forward navigation → re-read from URL.
  useEffect(() => {
    const onPop = () => setPeriod(readInitialPeriod());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const handleTogglePeriod = useCallback((next) => {
    if (!VALID_PERIODS.has(next) || next === period) return;
    setPeriod(next);
    track(PRICING_EVENTS.TOGGLE_CHANGED, {
      billing_period: next,
      currency,
      source_page: "landing",
    });
  }, [period, currency]);

  const handleCtaClick = useCallback((plan) => {
    const priceObj = plan.prices[period] || plan.prices.monthly;
    const priceAmount = priceObj?.amount ?? 0;
    // Free → straight to /signup, no query params (there is nothing to buy).
    if (plan.id === "free") {
      track(PRICING_EVENTS.PLAN_CLICKED, {
        plan: plan.id,
        billing_period: period,
        displayed_price: priceAmount,
        currency,
        source_page: "landing",
      });
      nav(user ? "/app" : "/signup");
      return;
    }

    // Paid plans → carry intent in query params (plan + period + currency).
    // Authenticated users go to /app/pricing for a final confirmation click.
    // Unauthenticated users go to /signup with the same params so intent
    // survives auth.
    const params = new URLSearchParams({
      plan: plan.id,
      period,
      currency,
    });

    track(PRICING_EVENTS.PLAN_CLICKED, {
      plan: plan.id,
      billing_period: period,
      displayed_price: priceAmount,
      currency,
      source_page: "landing",
    });

    if (user) {
      nav(`/app/pricing?${params.toString()}`);
    } else {
      nav(`/signup?${params.toString()}`);
    }
  }, [user, period, currency, nav]);

  return (
    <div className="dark min-h-screen bg-background text-foreground relative">
      {/* Nav */}
      <header className="glass-surface sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <BrandMark size={32} className="rounded-lg" />
            <span className="font-display font-bold tracking-tight text-lg">Career OS</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="landing-theme-toggle"
              className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {/*
              Header CTA — three states:
                1. Authenticated user (valid /auth/me)
                   → Open dashboard only (no redundant Log in).
                2. Returning visitor whose session is stale/absent
                   → Log in | Open dashboard.  Open dashboard is safe because
                   the /app tree is Protected — clicking it with no session
                   redirects to /login.
                3. Fresh visitor (never logged in on this browser)
                   → Log in | Get started.
              While auth is still resolving on first paint we render the
              variant we would render AFTER it resolves given the current
              returning-marker state — this avoids a visible flicker of
              "Get started" for a returning user.
            */}
            {user ? (
              <Button
                data-testid="nav-open-dashboard"
                size="sm"
                onClick={() => nav("/app")}
              >
                Open dashboard
              </Button>
            ) : isReturning ? (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  Log in
                </Link>
                <Button
                  data-testid="nav-open-dashboard"
                  size="sm"
                  onClick={() => nav("/app")}
                >
                  Open dashboard
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  Log in
                </Link>
                <Button size="sm" onClick={() => nav("/signup")} data-testid="nav-signup">Get started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero — premium background image with dark overlay for text readability */}
      <section
        className="relative overflow-hidden bg-[#0B0F19]"
        data-testid="hero-section"
      >
        {/* Background image layer. `object-cover` + `object-center-right` keeps
            the laptop focal point visible on wide screens while still framing
            the copy on the left. On very small screens the image drops to a
            more centered focus so the laptop doesn't get cropped off. */}
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_pathfinder-ai-149/artifacts/abobtxmj_Landing%20Page%20Image.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[75%_50%] md:object-[70%_50%] pointer-events-none select-none"
          data-testid="hero-bg-image"
          loading="eager"
          fetchPriority="high"
        />
        {/* Overlay stack — left→right gradient dims the copy area (so text
            stays crisp on the deep-blue backdrop) and a subtle bottom fade
            makes the transition into the Modules section feel continuous. */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]/20 md:to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-b from-transparent to-background" aria-hidden />

        <div className="max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-20 md:pb-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl"
          >
            <div
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm px-3 py-1 mb-8 text-xs font-mono uppercase tracking-widest text-white/70"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]"></span>
              Career operating system · Powered by Claude 4.5
            </div>
            <h1 className="hero-heading text-5xl md:text-7xl lg:text-8xl mb-8 text-white" data-testid="hero-heading">
              Your career,<br />
              <span className="text-white/60">managed</span>
              <span className="text-primary">.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-snug font-light mb-10">
              Not another job board. An <span className="text-white font-medium">AI Career Operating System</span> that tracks your worth, closes skill gaps, and engineers your next $100K.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-primary"
                size="lg"
                onClick={() => nav(user ? "/app" : "/signup")}
                className="gap-2"
              >
                Start free <ArrowRight size={16} />
              </Button>
              <Button variant="outline" size="lg" asChild data-testid="hero-cta-secondary" className="border-white/20 text-white hover:bg-white/10 hover:text-white backdrop-blur-sm">
                <a href="#features">See the modules</a>
              </Button>
            </div>
          </motion.div>

          {/* Value cards — outcomes, not marketing stats. Track → Plan → Grow
              → 24/7 tells the story the hero copy sets up. Same card layout
              and hover animation as the old stats strip. */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hero-stats">
            {[
              { k: "Track", v: "Know where you stand." },
              { k: "Plan", v: "Know what to do next." },
              { k: "Grow", v: "Reach your career goals." },
              { k: "24/7", v: "AI advisor on-call." },
            ].map((s, i) => (
              <motion.div
                key={s.v}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                whileHover={{ y: -4 }}
                className="border border-white/10 rounded-xl bg-white/[0.03] backdrop-blur-sm p-6 transition-shadow duration-300 hover:shadow-[0_0_40px_-12px_rgba(255,255,255,0.15)]"
                data-testid={`hero-value-card-${i}`}
              >
                <div className="font-mono font-semibold text-3xl md:text-4xl tracking-tight text-white">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-2 font-mono">{s.v}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="mb-14 max-w-3xl">
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">01 · Modules</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">
              Eight modules. One system.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Everything a professional coach, recruiter, salary analyst and learning advisor would do for you — running in the background.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { i: FileText, t: "Resume AI", d: "Drop your PDF. Our AI parses it, scores ATS compatibility, rewrites weak sections and tailors versions per job.", tid: "feat-resume" },
              { i: TrendingUp, t: "Salary Intelligence", d: "Real-time market data. See how underpaid you are, and what promotions, certs or moves are worth in dollars.", tid: "feat-salary" },
              { i: Route, t: "Career Roadmap", d: "A 5-step path from where you are to VP or founder — with required skills, salary jumps and probability of success.", tid: "feat-roadmap" },
              { i: MessageSquare, t: "AI Advisor", d: "Ask anything. Claude 4.5, remembers your context, tells you what to do next.", tid: "feat-advisor" },
              { i: Briefcase, t: "Job Tracker", d: "Kanban board for applications. Notes, contacts, interview stages, follow-ups. Never lose momentum.", tid: "feat-tracker" },
              { i: Sparkles, t: "Daily Insights", d: "Every morning, 5 specific insights: qualifications, cert lifts, follow-ups.", tid: "feat-insights" },
              { i: Shield, t: "Lifetime Earnings", d: "Project 20 years of income with and without the moves. Optimize for a career, not the next job.", tid: "feat-earnings" },
              { i: Crown, t: "Executive Track", d: "For those aiming Director+. Board readiness, comp benchmarks and equity packages.", tid: "feat-exec" },
            ].map((f, i) => (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="border border-border bg-card rounded-xl p-6 card-hover"
                data-testid={f.tid}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <f.i size={18} strokeWidth={1.75} />
                </div>
                <div className="font-display font-bold text-lg mb-2 tracking-tight">{f.t}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{f.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">02 · How it works</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-10 leading-tight">
              One profile. A lifetime of career growth.
            </h2>
            <div className="space-y-6">
              {[
                { n: "01", t: "Build Your Career Profile", d: "Upload your resume once. CareerOS analyzes your experience, skills, certifications, education, and work history to create your personalized career profile." },
                { n: "02", t: "Understand Your Market Value", d: "See how your salary compares to the current market. Identify opportunities to increase your earning potential and close skill gaps." },
                { n: "03", t: "Follow Your Career Roadmap", d: "Receive a personalized step-by-step plan with recommended skills, certifications, salary milestones, and the actions needed to reach your career goals." },
                { n: "04", t: "Grow Every Week", d: "Work with your AI Career Advisor to track your progress, stay accountable, and receive personalized recommendations as your career evolves." },
              ].map((s) => (
                <div key={s.n} className="flex gap-6 border-t border-border pt-6">
                  <div className="font-mono text-2xl text-primary font-semibold">{s.n}</div>
                  <div>
                    <div className="font-display font-bold text-lg mb-1 tracking-tight">{s.t}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-border">
            <img
              src="https://images.unsplash.com/photo-1577415124269-fc1140a69e91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjBwcm9mZXNzaW9uYWxzJTIwdGFsa2luZ3xlbnwwfHx8fDE3ODM0NDc2NTl8MA&ixlib=rb-4.1.0&q=85"
              alt="Professionals"
              className="w-full grayscale contrast-125"
            />
            <div className="absolute bottom-4 left-4 right-4 glass-surface p-4 rounded-lg">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Live market signal</div>
              <div className="font-display font-bold text-base mt-1">Senior PMs in SF earn $187K median (+9% YoY)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">03 · Pricing</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">
              Pick your plan. Cancel anytime.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Start free. Upgrade when you&apos;re ready for unlimited AI, advanced planning, and executive-tier strategy.
            </p>
          </div>

          {/* Toggle row: Monthly/Annual + Currency selector */}
          <div className="mb-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <div
              role="tablist"
              aria-label="Billing period"
              className="relative inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-sm"
              data-testid="pricing-period-toggle"
            >
              <PeriodTab
                value="monthly"
                current={period}
                onSelect={handleTogglePeriod}
                label="Monthly"
                testid="pricing-toggle-monthly"
              />
              <PeriodTab
                value="yearly"
                current={period}
                onSelect={handleTogglePeriod}
                label="Annual"
                badge={pricingData?.plans?.find((p) => p.tier === "pro")?.yearly_savings_pct
                  ? `Save ${pricingData.plans.find((p) => p.tier === "pro").yearly_savings_pct}%`
                  : "Save 17%"}
                testid="pricing-toggle-yearly"
              />
            </div>

            <CurrencyPicker
              currency={currency}
              supported={pricingData?.supported || []}
              source={pricingData?.source}
              onChange={handleCurrencyChange}
              disabled={pricingLoading}
            />
          </div>

          {pricingError && (
            <div className="mb-6 text-center text-sm text-muted-foreground" data-testid="pricing-error-fallback">
              Couldn&apos;t load localized pricing — showing base USD. You can pick a currency above.
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6 items-stretch" data-testid="pricing-cards">
            {pricingLoading && plansForRender.length === 0
              ? Array.from({ length: 3 }).map((_, i) => <PricingCardSkeleton key={i} index={i} />)
              : plansForRender.map((plan, i) => (
                  <PricingCard
                    key={plan.id}
                    plan={plan}
                    period={period}
                    currency={currency}
                    locale={locale}
                    index={i}
                    onCta={() => handleCtaClick(plan)}
                  />
                ))}
          </div>

          {pricingData?.source && pricingData.source !== "override" && (
            <div className="mt-6 text-center text-xs text-muted-foreground font-mono" data-testid="pricing-locale-hint">
              Prices shown in {currency}
              {pricingData.country_code ? ` · detected ${pricingData.country_code}` : ""}
              {" · "}
              <button
                type="button"
                onClick={() => handleCurrencyChange("USD")}
                className="underline hover:text-foreground transition-colors"
                data-testid="pricing-reset-to-usd"
              >
                Show USD
              </button>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">04 · FAQ</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">
              Frequently asked.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Answers to the questions we hear most often. Have another? Drop us a line — we usually reply within a day.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full space-y-2"
            data-testid="faq-accordion"
          >
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`faq-${i}`}
                className="border border-border rounded-xl bg-card overflow-hidden"
              >
                <AccordionTrigger
                  className="px-5 py-4 text-left font-display font-semibold text-base md:text-lg hover:no-underline hover:bg-accent/40 transition-colors"
                  data-testid={`faq-trigger-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="px-5 pb-5 text-sm md:text-base text-muted-foreground leading-relaxed"
                  data-testid={`faq-content-${i}`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}

// ---------- Period toggle tab -----------
function PeriodTab({ value, current, onSelect, label, badge, testid }) {
  const active = current === value;
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={() => onSelect(value)}
      data-testid={testid}
      className={`relative z-10 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors ${
        active
          ? "text-primary-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {active && (
        <motion.span
          layoutId="pricing-period-pill"
          className="absolute inset-0 -z-10 rounded-full bg-primary shadow-md"
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
      )}
      <span>{label}</span>
      {badge && (
        <span
          className={`text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 ${
            active
              ? "bg-primary-foreground/20 text-primary-foreground"
              : "bg-[hsl(var(--success))/0.12] text-[hsl(var(--success))]"
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// ---------- Currency Picker -----------
function CurrencyPicker({ currency, supported, source, onChange, disabled }) {
  // Swapped native <select> → shadcn/Radix Select so the dropdown popup fully
  // inherits our dark theme (native selects render OS-white on dark themes).
  // Keeps all functionality: currency detection, onChange, source badge,
  // disabled state, keyboard nav, ARIA — plus AA-contrast dark styling with
  // a primary-color accent on hover / focus / selected.
  const options = supported.length > 0 ? supported : [{ code: currency, name: currency }];
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 shadow-sm transition-colors duration-200 focus-within:border-primary focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.18)]"
      data-testid="pricing-currency-picker"
    >
      <Globe size={14} className="text-muted-foreground flex-shrink-0" aria-hidden />
      <label htmlFor="pricing-currency-select" className="sr-only">Currency</label>
      <Select value={currency} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          id="pricing-currency-select"
          data-testid="pricing-currency-select"
          aria-label={`Pricing currency, currently ${currency}`}
          className="h-8 min-w-[7.5rem] border-0 bg-transparent px-1 text-sm font-medium text-foreground shadow-none hover:text-primary focus:ring-0 focus:ring-offset-0 focus:outline-none data-[state=open]:text-primary transition-colors duration-200 cursor-pointer disabled:opacity-50 [&_svg]:opacity-70 [&_svg]:transition-transform [&[data-state=open]_svg]:rotate-180"
        >
          <SelectValue placeholder="Currency" />
        </SelectTrigger>
        <SelectContent
          className="min-w-[14rem] max-h-[18rem] rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl backdrop-blur-md"
          data-testid="pricing-currency-menu"
          position="popper"
          sideOffset={8}
        >
          {options.map((c) => (
            <SelectItem
              key={c.code}
              value={c.code}
              data-testid={`pricing-currency-option-${c.code}`}
              className="cursor-pointer rounded-md text-sm py-2 pr-2 pl-8 focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/15 data-[state=checked]:text-primary data-[state=checked]:font-semibold transition-colors duration-150"
            >
              <span className="font-mono font-semibold mr-2">{c.code}</span>
              <span className="text-muted-foreground">· {c.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {source && source !== "override" && (
        <span
          className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex-shrink-0"
          data-testid="pricing-currency-source"
        >
          auto
        </span>
      )}
    </div>
  );
}

// ---------- Skeleton (prevents USD-then-flash) ----------
function PricingCardSkeleton({ index }) {
  return (
    <div
      className="rounded-2xl bg-card border border-border p-7 animate-pulse"
      data-testid={`pricing-skeleton-${index}`}
      aria-hidden
    >
      <div className="h-4 w-20 bg-muted rounded mb-4" />
      <div className="h-12 w-32 bg-muted rounded mb-2" />
      <div className="h-3 w-24 bg-muted rounded mb-8" />
      <div className="space-y-2.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-3 bg-muted rounded" style={{ width: `${60 + ((i * 7) % 40)}%` }} />
        ))}
      </div>
      <div className="h-10 w-full bg-muted rounded mt-8" />
    </div>
  );
}

// ---------- Pricing Card ---------------
function PricingCard({ plan, period, currency, locale, index, onCta }) {
  const highlight = plan.highlight;
  const priceObj = plan.prices?.[period] || plan.prices?.monthly || { amount: 0 };
  const isFree = plan.id === "free";

  const amount = priceObj.amount ?? 0;
  const priceLabel = isFree
    ? formatMoney(0, currency, locale, { decimals: 0 })
    : formatMoney(amount, currency, locale, {
        // Whole-number prices → no cents. e.g. $25 not $25.00.
        decimals: Number.isInteger(amount) ? 0 : 2,
      });
  const periodLabel = isFree
    ? "/month"
    : period === "monthly"
      ? `${currency} / month`
      : `${currency} / year`;

  const eff = period === "yearly" ? plan.effective_monthly_yearly : null;
  const savings = period === "yearly" ? plan.yearly_savings_amount : null;
  const savingsPct = plan.yearly_savings_pct;
  // For the monthly view, show the "save $X on annual" upsell (uses the
  // savings amount computed by the backend).
  const monthlyUpsell = period === "monthly" && !isFree && plan.yearly_savings_amount > 0
    ? plan.yearly_savings_amount
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      className={`relative flex flex-col rounded-2xl bg-card p-7 transition-shadow duration-300 ${
        highlight
          ? "border-2 border-amber-400/60 shadow-[0_0_40px_-8px_rgba(251,191,36,0.4)] hover:shadow-[0_0_60px_-8px_rgba(251,191,36,0.55)]"
          : "border border-border shadow-sm hover:shadow-lg"
      }`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-black px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-semibold shadow-lg">
            <Star size={11} fill="currentColor" /> Most Popular
          </span>
        </div>
      )}

      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
          {plan.name}
        </div>

        <div className="min-h-[76px]" data-testid={`pricing-price-block-${plan.id}`}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${plan.id}-${period}-${currency}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="font-display font-black text-5xl md:text-6xl tracking-tight"
                  data-testid={`pricing-amount-${plan.id}`}
                >
                  {priceLabel}
                </span>
                <span className="text-sm text-muted-foreground">{periodLabel}</span>
              </div>
              {eff !== null && eff !== undefined && (
                <div
                  className="mt-1 text-xs text-muted-foreground font-mono"
                  data-testid={`pricing-effective-${plan.id}`}
                >
                  ≈ {formatMoney(eff, currency, locale)} /mo · billed annually
                </div>
              )}
              {monthlyUpsell && (
                <div
                  className="mt-1 text-xs text-[hsl(var(--success))] font-mono"
                  data-testid={`pricing-annual-hint-${plan.id}`}
                >
                  Save {formatMoney(monthlyUpsell, currency, locale, { decimals: 0 })}/yr on annual
                </div>
              )}
              {savings > 0 && (
                <div
                  className="mt-1 text-xs text-[hsl(var(--success))] font-mono"
                  data-testid={`pricing-savings-${plan.id}`}
                >
                  Save {formatMoney(savings, currency, locale, { decimals: 0 })}/yr vs monthly
                  {savingsPct ? ` (${savingsPct}%)` : ""}
                </div>
              )}
              {isFree && (
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  No credit card required
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <ul className="mt-8 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className={`flex gap-2.5 text-sm leading-relaxed ${
            f.endsWith(":") ? "text-foreground font-semibold pt-1" : "text-muted-foreground"
          }`}>
            {!f.endsWith(":") && (
              <Check size={16} className="text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
            )}
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onCta}
        size="lg"
        variant={highlight ? "default" : "outline"}
        className="mt-8 w-full gap-2"
        data-testid={`pricing-cta-${plan.id}`}
      >
        {plan.cta} <ArrowRight size={14} />
      </Button>
    </motion.div>
  );
}
